import { Component } from '@theme/component';

/**
 * SD Quick Carousel Component
 *
 * A lightweight single-axis carousel using CSS scroll-snap.
 * Supports autoplay with pause on hover/focus, single-item mode,
 * and theme-aware dot navigation.
 *
 * @typedef {Object} SDQuickCarouselRefs
 * @property {HTMLElement} scroller - Scrollable container
 * @property {HTMLElement} [dots] - Dot navigation container
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements
 *
 * @extends {Component<SDQuickCarouselRefs>}
 */
class SDQuickCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLButtonElement[]} */
  #dotButtons = [];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {number|null} */
  #autoplayTimer = null;

  connectedCallback() {
    super.connectedCallback();

    const scroller = this.refs.scroller;
    if (!scroller) return;

    this.#slides = Array.from(scroller.children);
    if (!this.#slides.length) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#setupSlideAttributes();
    this.#setupDots(signal);
    this.#setupScrollListener(signal);
    this.#setupAutoplay(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
    this.#abortController?.abort();
  }

  // ==========================================================================
  // Public Methods
  // ==========================================================================

  /**
   * Navigate to a specific slide
   * @param {number} index - Target slide index
   */
  select(index) {
    const target = this.#slides[index];
    const scroller = this.refs.scroller;
    if (!target || !scroller) return;

    // Scroll the inner scroller horizontally only. scrollIntoView would also
    // scroll the page vertically when the carousel is off-screen.
    const scrollerRect = scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    scroller.scrollTo({
      left: scroller.scrollLeft + (targetRect.left - scrollerRect.left),
      behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.#setCurrentIndex(index);
  }

  // ==========================================================================
  // Private Methods
  // ==========================================================================

  /** @returns {boolean} */
  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Get active slide index from scroll position
   * @returns {number}
   */
  #getScrollIndex() {
    const scroller = this.refs.scroller;
    if (!scroller || !this.#slides.length) return 0;

    const scrollPos = scroller.scrollLeft;
    const gap = parseFloat(getComputedStyle(scroller).gap) || 0;
    const slideWidth = this.#slides[0].offsetWidth + gap;

    return Math.round(scrollPos / slideWidth);
  }

  /** Add ARIA attributes to each slide */
  #setupSlideAttributes() {
    const total = this.#slides.length;
    const scrollerId = this.refs.scroller.id || `scroller-${this.id}`;

    for (const [i, slide] of this.#slides.entries()) {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${i + 1} of ${total}`);
      slide.setAttribute('id', `${scrollerId}-slide-${i}`);
    }
  }

  /**
   * Create dot buttons dynamically (slide count unknown at render time)
   * @param {AbortSignal} signal
   */
  #setupDots(signal) {
    const dotsContainer = this.refs.dots;
    if (!dotsContainer) return;

    const total = this.#slides.length;
    const scrollerId = this.refs.scroller.id || `scroller-${this.id}`;

    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.className = 'sd-quick-carousel__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dot.setAttribute('aria-controls', `${scrollerId}-slide-${i}`);
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('tabindex', i === 0 ? '0' : '-1');

      dot.addEventListener('click', () => this.select(i), { signal });
      dotsContainer.appendChild(dot);
    }

    this.#dotButtons = Array.from(dotsContainer.querySelectorAll('.sd-quick-carousel__dot'));

    // Keyboard navigation for dots
    dotsContainer.addEventListener('keydown', (e) => this.#handleDotKeydown(e), { signal });
  }

  /**
   * Arrow key navigation within dot tablist
   * @param {KeyboardEvent} e
   */
  #handleDotKeydown(e) {
    const total = this.#slides.length;
    let nextIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        nextIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : total - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        nextIndex = this.#currentIndex < total - 1 ? this.#currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = total - 1;
        break;
      default:
        return;
    }

    this.#dotButtons[nextIndex]?.focus();
    this.select(nextIndex);
  }

  /**
   * Track scroll position to update active dot
   * @param {AbortSignal} signal
   */
  #setupScrollListener(signal) {
    const scroller = this.refs.scroller;
    if (!scroller) return;

    scroller.addEventListener(
      'scroll',
      () => {
        requestAnimationFrame(() => {
          const index = this.#getScrollIndex();
          if (index !== this.#currentIndex) {
            this.#setCurrentIndex(index);
          }
        });
      },
      { signal, passive: true }
    );
  }

  /**
   * Update current index and sync dots + live region
   * @param {number} index
   */
  #setCurrentIndex(index) {
    this.#currentIndex = index;

    for (const [i, dot] of this.#dotButtons.entries()) {
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      dot.setAttribute('tabindex', i === index ? '0' : '-1');
    }

    const { liveRegion } = this.refs;
    if (liveRegion) {
      liveRegion.textContent = `Slide ${index + 1} of ${this.#slides.length}`;
    }
  }

  /**
   * Setup autoplay from data attribute
   * @param {AbortSignal} signal
   */
  #setupAutoplay(signal) {
    const speed = this.dataset.autoplay;
    if (!speed || this.#prefersReducedMotion()) return;

    const interval = parseInt(speed, 10) * 1000;

    const start = () => {
      this.#stopAutoplay();
      this.#autoplayTimer = setInterval(() => {
        const next = (this.#currentIndex + 1) % this.#slides.length;
        this.select(next);
      }, interval);
    };

    const stop = () => this.#stopAutoplay();

    this.addEventListener('mouseenter', stop, { signal });
    this.addEventListener('mouseleave', start, { signal });
    this.addEventListener('focusin', stop, { signal });
    this.addEventListener(
      'focusout',
      (e) => {
        if (!this.contains(/** @type {Node} */ (e.relatedTarget))) {
          start();
        }
      },
      { signal }
    );

    start();
  }

  /** Stop autoplay timer */
  #stopAutoplay() {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = null;
    }
  }
}

if (!customElements.get('sd-quick-carousel')) {
  customElements.define('sd-quick-carousel', SDQuickCarousel);
}
